import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import useUserStore from '../../stores/useUserStore';
import DroneHistory from './DroneHistory';
import { LogoSpinner } from '../../components/ui/LogoSpinner';
import { useApi } from '../../apiHelpers';
import { useToast } from '../../components/Toast/ToastContext';
import { clearFlag, clearFlagErrorMessage } from '../../utils/flag-utils';
import DroneHeader from './DroneHeader';
import DroneStatus from './DroneStatus';
import DroneInfo from './DroneInfo';
import DroneActions from './DroneActions';
import RMADetails from '../../RMA/RMADetails';
import useDesktop from '../../utils/useDesktop';
import { buttonClass } from '../../components/ui/Button';

function Drone() {
    const { serialnumber: routeSerialNumber } = useParams();
    const history = useHistory();
    const user = useUserStore((state) => state.user);
    const api = useApi();
    const toast = useToast();
    const isDesktop = useDesktop();

    // Derive serialNumber from URL param (not state) so it updates on navigation
    const serialNumber = routeSerialNumber.toUpperCase();
    const [drone, setDrone] = useState({});
    const [partsReady, setPartsReady] = useState(false);
    const [flag, setFlag] = useState('');
    const [showPopUp, setShowPopUp] = useState(false);
    const [updatedDrone, setUpdatedDrone] = useState({});
    const [serialReadable, setSerialReadable] = useState('');
    const [selectedRMANumber, setSelectedRMANumber] = useState(null);
    const [activeRMA, setActiveRMA] = useState(null);

    const [reload, setReload] = useState(false);

    // View settings - persisted in localStorage
    const [showQRKey, setShowQRKey] = useState(() => {
        const saved = localStorage.getItem('dronePageShowQRKey');
        return saved === 'true'; // Default to false (hidden)
    });

    const toggleShowQRKey = () => {
        setShowQRKey(prev => {
            const newValue = !prev;
            localStorage.setItem('dronePageShowQRKey', newValue.toString());
            return newValue;
        });
    };

    useEffect(() => {
        let isMounted = true;

        // Reset state when drone changes
        setPartsReady(false);
        setSelectedRMANumber(null);
        setActiveRMA(null);

        const fetchDroneData = api.get(`/fleet/${serialNumber}`);
        const fetchFlagData = api.get(`/flags/${serialNumber}`);
        const fetchRMAData = api.get(`/rma/drone/${serialNumber}`, { silent: true }).catch(() => []);

        // Run all fetches concurrently:
        Promise.all([fetchDroneData, fetchFlagData, fetchRMAData])
            .then(([droneData, flags, rmas]) => {
                if (isMounted) {
                    setDrone(droneData);
                    setPartsReady(true);
                    // Set flag based on drone condition and flags:
                    if (droneData.condition === "NYC") {
                        setFlag("Not yet commissioned!");
                    } else if (!flags[0] || flags[0].active === false) {
                        setFlag("Ready to fly!");
                    } else {
                        setFlag(flags[0].descript);
                    }
                    // Find active RMA for this drone (not completed/rejected)
                    const active = (rmas || []).find(rma =>
                        rma.item_status === 'pending_review' ||
                        (rma.item_status === 'approved' && !['completed', 'unrepairable'].includes(rma.repair_status))
                    );
                    setActiveRMA(active || null);
                    // Fetch serial number data using droneData.serialnumber:
                    return api.get(`/serialnumbers/${droneData.serialnumber}`);
                }
            })
            .then(serialdata => {
                if (isMounted) {
                    setSerialReadable(serialdata.dronereadable);
                }
            })
            .catch(error => {
                if (isMounted) toast.error('Drone not found, please try again');
            });

        return () => { isMounted = false; }
    }, [serialNumber, reload]);

    const toggleFlagPopup = () => {
        setShowPopUp(prev => !prev);
    };

    // DRY helper: Toggle between a status and RTF
    const toggleDroneStatus = (targetStatus) => {
        const { serialnumber, condition } = drone;

        if (condition !== targetStatus) {
            // Open popup to create Flag or OOC
            const updated = {
                drone_serial: serialnumber,
                condition: targetStatus,
                active: true
            };
            setUpdatedDrone(updated);
            toggleFlagPopup();
        } else {
            // Already at targetStatus, clear it to RTF
            const updated = {
                drone_serial: serialnumber,
                condition: 'RTF',
                active: false
            };
            setUpdatedDrone(updated);
            unflagAction(updated);
        }
    };

    const flagDrone = () => toggleDroneStatus('Flag');
    const oocDrone = () => toggleDroneStatus('OOC');

    const updateState = newStatus => {
        setReload(prev => !prev);
    };

    const unflagAction = async (updated) => {
        try {
            // Atomic workflow: clears flag and resets drone in ONE transaction
            await clearFlag(api, drone.serialnumber);
            // Trigger local reload - fleet will refresh when user navigates there
            setReload(prev => !prev);
        } catch (error) {
            toast.error(clearFlagErrorMessage(error));
        }
    };

    const handleRMAClick = (rmaNumber) => {
        if (isDesktop) {
            // Desktop: Show split view
            setSelectedRMANumber(rmaNumber);
        } else {
            // Mobile: Navigate to RMA details page
            history.push(`/rma/${rmaNumber}`);
        }
    };

    if (!partsReady) {
        return <LogoSpinner label="Loading the drone" />;
    }

    return (
        <div className="flex w-full items-start mt-4">
            {/* Main Drone Page - 1/4 width when RMA is open */}
            <div
                className={`transition-all duration-500 drone_page ${
                    selectedRMANumber && isDesktop ? 'w-1/4' : 'w-full'
                }`}
                style={{ marginTop: 0 }}
            >
                <DroneHeader drone={drone} />
                <DroneStatus
                    drone={drone}
                    flag={flag}
                    showPopUp={showPopUp}
                    updatedDrone={updatedDrone}
                    toggleFlagPopup={toggleFlagPopup}
                    flagDrone={flagDrone}
                    oocDrone={oocDrone}
                    updateState={updateState}
                    userid={user.id}
                    activeRMA={activeRMA}
                    onRMAClick={handleRMAClick}
                />
                <DroneHistory
                    drone_serial={serialNumber}
                    onRMAClick={handleRMAClick}
                />
                <DroneInfo
                    drone={drone}
                    serialReadable={serialReadable}
                    serialNumber={serialNumber}
                    isDesktop={isDesktop}
                    showQRKey={showQRKey}
                />
                <DroneActions
                    drone={drone}
                    serialNumber={serialNumber}
                    showQRKey={showQRKey}
                    onToggleQRKey={toggleShowQRKey}
                    onDroneUpdated={() => setReload(prev => !prev)}
                />
            </div>

            {/* RMA Details Panel - 3/4 width, slides in from right */}
            {selectedRMANumber && isDesktop && (
                <div className="w-3/4 overflow-y-auto
                    border-l border-primary shadow-[-4px_0_12px_rgba(255,204,51,0.2)]
                    animate-slide-in-right">
                    <div className="sticky top-0 bg-[#161616] z-10 p-4 border-b border-gray-700">
                        <button
                            onClick={() => setSelectedRMANumber(null)}
                            className={buttonClass('tonal', 'md')}
                        >
                            Close RMA Details
                        </button>
                    </div>
                    <div className="p-4">
                        <RMADetails rmaNumberOverride={selectedRMANumber} highlightSerial={serialNumber} />
                    </div>
                </div>
            )}
        </div>
    );
}

export default Drone;
